import { supabase } from '@/lib/supabase';

/**
 * API Interceptor that ensures requests are only made when authentication is fully established
 * This prevents 401 errors during auth initialization by queuing requests until auth is ready
 */

// State tracking
let authReady = false;
const requestQueue: Array<() => void> = [];

// Store original fetch implementation - use window.fetch for browser environments
const originalFetch = window.fetch;
// Add TypeScript declaration for window.originalFetch
declare global {
  interface Window {
    originalFetch: typeof fetch;
  }
}
// Expose the original fetch globally for debugging and bypassing the interceptor
window.originalFetch = originalFetch;

// Initialize interceptor
export const initializeApiInterceptor = () => {
  console.log('Initializing API interceptor');
  
  // Patch global fetch to intercept Supabase API calls
  patchGlobalFetch();
  
  // Set up auth state change listener
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
    console.log(`Auth state changed: ${event}`);
    
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      // Add a small delay to ensure token is fully processed
      setTimeout(() => {
        authReady = true;
        processQueue();
        console.log('Auth ready, processed queued requests:', requestQueue.length);
      }, 800); // Increased from 500ms to 800ms for more safety margin
    } else if (event === 'SIGNED_OUT') {
      authReady = false;
      console.log('Auth signed out, requests will be queued');
    }
  });
  
  // Check if already signed in
  checkCurrentAuth();
  
  return subscription;
};

// Override global fetch to intercept all Supabase API calls
const patchGlobalFetch = () => {
  // @ts-ignore - Overriding fetch
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = input instanceof Request ? input.url : input.toString();
    // Only intercept Supabase REST or Auth API calls, NOT Edge Functions
    if (
      url.includes('supabase.co') &&
      !url.includes('/functions/v1/') // Allow Edge Functions to bypass
    ) {
      return await ensureAuthReady(() => originalFetch(input, init));
    }
    // For all other requests (including Edge Functions), use original fetch
    return originalFetch(input, init);
  };
};

// Check if already authenticated
const checkCurrentAuth = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session) {
    console.log('User already authenticated, setting auth ready');
    authReady = true;
    processQueue();
  }
};

// Process all queued requests
const processQueue = () => {
  if (requestQueue.length > 0) {
    console.log(`Processing ${requestQueue.length} queued requests`);
    requestQueue.forEach(fn => fn());
    requestQueue.length = 0; // Clear the queue
  }
};

/**
 * Ensures a function that makes API calls only runs when auth is ready
 * If auth is not ready, the function will be queued and executed when auth is established
 * 
 * @param fn Function to execute (typically an API call)
 * @returns Promise that resolves with the result of fn
 */
export const ensureAuthReady = async <T>(fn: () => Promise<T>): Promise<T> => {
  if (authReady) {
    return fn();
  }
  
  console.log('Auth not ready, queueing request');
  return new Promise<T>((resolve) => {
    requestQueue.push(async () => {
      const result = await fn();
      resolve(result);
    });
  });
};

// Helper for wrapping Supabase queries
export const withAuth = <T>(supabaseQuery: () => Promise<T>): Promise<T> => {
  return ensureAuthReady(supabaseQuery);
}; 